// src/pages/DashboardPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { getSubjects, getClassrooms, getCurrentYear, getLevels, getClassroomSettings, getSchoolSettings, getQualityTypes, getQualityProgress, getSchedules, getHolidays, getPeriods } from '../services/api';
import { classroomLabel, levelCodePrefix } from '../utils/classroom';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const ROLE_LABELS = {
  admin: 'ผู้ดูแลระบบ',
  academic: 'ครูวิชาการ',
  assessment: 'ครูวัดผล',
  teacher: 'ครูผู้สอน',
  homeroom: 'ครูประจำชั้น',
};

// ---- helpers เหมือน ClassroomsPage ----
const _GRADE_KEY_DISPLAY = {
  k1:1,k2:2,k3:3,p1:1,p2:2,p3:3,p4:4,p5:5,p6:6,m1:1,m2:2,m3:3,h1:4,h2:5,h3:6,
};
const _ACTIVE_KEYS_MAP = {
  'k1-p6':['k1','k2','k3','p1','p2','p3','p4','p5','p6'],
  'k2-p6':['k2','k3','p1','p2','p3','p4','p5','p6'],
  'k1-m3':['k1','k2','k3','p1','p2','p3','p4','p5','p6','m1','m2','m3'],
  'k2-m3':['k2','k3','p1','p2','p3','p4','p5','p6','m1','m2','m3'],
  'p1-p6':['p1','p2','p3','p4','p5','p6'],
  'p1-m3':['p1','p2','p3','p4','p5','p6','m1','m2','m3'],
  'm1-m3':['m1','m2','m3'],
  'm1-h3':['m1','m2','m3','h1','h2','h3'],
  'h1-h3':['h1','h2','h3'],
  'p1-h3':['p1','p2','p3','p4','p5','p6','m1','m2','m3','h1','h2','h3'],
  'all':['k1','k2','k3','p1','p2','p3','p4','p5','p6','m1','m2','m3','h1','h2','h3'],
};
function _buildKeyMap(levelList) {
  const kL = levelList.find(l => /อนุบาล|kindergarten/i.test(l.level_name) || /^อ$/i.test(l.level_code));
  const pL = levelList.find(l => /ประถม|primary/i.test(l.level_name) || /^ป$/i.test(l.level_code));
  const mL = levelList.find(l => /ต้น|junior|middle/i.test(l.level_name) || /ม\.ต้น/i.test(l.level_code));
  const hL = levelList.find(l => /ปลาย|senior|high/i.test(l.level_name) || /ม\.ปลาย/i.test(l.level_code));
  const mk = (lvl, g, dg) => lvl ? { level_id: lvl.id, level_code: lvl.level_code, grade: g, displayGrade: dg } : null;
  return {
    k1:mk(kL,1,1),k2:mk(kL,2,2),k3:mk(kL,3,3),
    p1:mk(pL,1,1),p2:mk(pL,2,2),p3:mk(pL,3,3),p4:mk(pL,4,4),p5:mk(pL,5,5),p6:mk(pL,6,6),
    m1:mk(mL,1,1),m2:mk(mL,2,2),m3:mk(mL,3,3),
    h1:mk(hL,4,4),h2:mk(hL,5,5),h3:mk(hL,6,6),
  };
}
function generateDisplayRooms(gradeRange, activeSettings, levelList, dbClassrooms) {
  const gradeKeys = _ACTIVE_KEYS_MAP[gradeRange];
  if (!gradeKeys || activeSettings.length === 0) return [];
  const keyMap = _buildKeyMap(levelList);
  const setting = activeSettings[0];
  const rows = [];
  for (const gk of gradeKeys) {
    const info = keyMap[gk];
    if (!info) continue;
    const roomCount = Number(setting[`rooms_${gk}`] || 0);
    if (roomCount === 0) continue;
    const dg = _GRADE_KEY_DISPLAY[gk];
    for (let room = 1; room <= roomCount; room++) {
      const dbRoom = dbClassrooms.find(
        c => Number(c.level_id) === Number(info.level_id)
          && Number(c.grade) === Number(info.grade)
          && Number(c.room) === room
      );
      rows.push({
        key: `${gk}_${room}`,
        label: `${levelCodePrefix(info.level_code)}${dg}/${room}`,
        level_id: info.level_id, level_code: info.level_code,
        grade: info.grade, displayGrade: dg, room,
        id: dbRoom?.id,
        homeroom1_name: dbRoom?.homeroom1_name,
        homeroom2_name: dbRoom?.homeroom2_name,
        student_count: dbRoom?.student_count || 0,
        homeroom_teacher_id: dbRoom?.homeroom_teacher_id,
        homeroom_teacher2_id: dbRoom?.homeroom_teacher2_id,
        level_name: dbRoom?.level_name || '',
      });
    }
  }
  return rows;
}
// ----------------------------------------

export default function DashboardPage() {
  const { user, isAcademic } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [displayRooms, setDisplayRooms] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [qTypes, setQTypes] = useState([]);
  const [qualProgress, setQualProgress] = useState({});
  const [filterGrade, setFilterGrade] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [isSem2, setIsSem2] = useState(false);
  const [loading, setLoading] = useState(true);
  const [todayLessons, setTodayLessons] = useState([]);

  // Helper: determine semester from date + classroom_settings
  const detectSemester = (settings, date) => {
    for (const s of settings) {
      if (s.start_date && s.end_date && date >= s.start_date && date <= s.end_date) return Number(s.team);
    }
    const sorted = [...settings].filter(s => s.start_date).sort((a, b) => b.start_date.localeCompare(a.start_date));
    for (const s of sorted) { if (date >= s.start_date) return Number(s.team); }
    return 1;
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        // ── Batch 1: โหลดข้อมูลพื้นฐานทั้งหมดพร้อมกัน (ลด waterfall) ──
        const [yearRes, subRes, lvlRes, sSettings, clsRes, stRes] = await Promise.all([
          getCurrentYear(),
          getSubjects({ teacher_id: isAcademic() ? undefined : user?.id }),
          getLevels(),
          getSchoolSettings().catch(() => ({ data: { data: null } })),
          getClassrooms({ academic_year_id: '__current__' }).catch(() => ({ data: { data: [] } })),
          getClassroomSettings({ academic_year_id: '__current__' }).catch(() => ({ data: { data: [] } })),
        ]);
        const year = yearRes.data.success ? yearRes.data.data : null;
        if (year) setCurrentYear(year);
        const subList = subRes.data.success ? subRes.data.data : [];
        setSubjects(subList);

        const ss = sSettings.data.data || {};
        const isLockedFn = (v, dt) => (v === 1 || v === '1' || v === true) || (dt && new Date() >= new Date(dt));
        setIsSem2(isLockedFn(ss.lock_sem1, ss.lock_date_sem1));

        const levelList = lvlRes.data.data || [];
        const gradeRange = ss.grade_range || '';

        // ── สร้าง displayRooms จากข้อมูลที่โหลดมาแล้ว (ไม่ต้อง await อีก) ──
        const dbClassrooms = clsRes.data.data || [];
        const activeSettings = (stRes.data.data || []).filter(s => Number(s.is_active) === 1);
        const rooms = generateDisplayRooms(gradeRange, activeSettings, levelList, dbClassrooms);
        setDisplayRooms(rooms.length > 0 ? rooms : dbClassrooms.map(c => ({
          key: `db_${c.id}`, label: classroomLabel(c),
          id: c.id, level_id: c.level_id, level_code: c.level_code,
          grade: c.grade, displayGrade: c.grade, room: c.room,
          homeroom1_name: c.homeroom1_name, homeroom2_name: c.homeroom2_name,
          student_count: c.student_count || 0,
          homeroom_teacher_id: c.homeroom_teacher_id,
          homeroom_teacher2_id: c.homeroom_teacher2_id,
          level_name: c.level_name || '',
        })));

        // ── Batch 2: โหลดข้อมูลเสริม พร้อมกัน (ไม่ block หน้าจอหลัก) ──
        const batch2 = [];

        // Quality types
        if (year) {
          batch2.push(
            getQualityTypes({ academic_year_id: year.id })
              .then(qtRes => {
                const qt = (qtRes.data.data || []).filter(t => Number(t.is_enabled) === 1);
                setQTypes(qt);
              })
              .catch(() => {})
          );
        }

        // Quality progress — 1 request แทนวน loop ทีละห้อง
        if (year && subList.length > 0) {
          const cids = [...new Set(subList.map(s => s.classroom_id).filter(Boolean))];
          if (cids.length > 0) {
            batch2.push((async () => {
              try {
                const r = await getQualityProgress({ classroom_ids: cids.join(','), academic_year_id: year.id });
                if (r.data.success) {
                  const qpMap = {};
                  (r.data.data || []).forEach(d => {
                    if (!qpMap[d.subject_id]) qpMap[d.subject_id] = {};
                    if (!qpMap[d.subject_id][d.type_key]) qpMap[d.subject_id][d.type_key] = {};
                    qpMap[d.subject_id][d.type_key][d.semester] = Number(d.scored_count) || 0;
                  });
                  setQualProgress(qpMap);
                }
              } catch {}
            })());
          }
        }

        // ตารางสอนวันนี้ (สำหรับครู)
        if (year && user?.role === 'teacher') {
          batch2.push((async () => {
            try {
              const allSemSettings = stRes.data.data || [];
              const today = new Date().toISOString().slice(0, 10);
              const sem = detectSemester(allSemSettings, today);

              const [schRes, holRes, perRes] = await Promise.all([
                getSchedules({ academic_year_id: year.id, teacher_id: user.id, semester: sem }),
                getHolidays({ academic_year_id: year.id }),
                getPeriods({ academic_year_id: year.id }),
              ]);
              let schedules = schRes.data.data || [];

              if (schedules.length === 0) {
                const otherSem = sem === 1 ? 2 : 1;
                try {
                  const schRes2 = await getSchedules({ academic_year_id: year.id, teacher_id: user.id, semester: otherSem });
                  schedules = schRes2.data.data || [];
                } catch { /* ignore */ }
              }

              const holidays = holRes.data.data || [];
              const periodsList = perRes.data.data || [];
              const dateObj = new Date(today + 'T00:00:00');
              const rawDow = dateObj.getDay();
              const holidayInfo = holidays.find(h => h.holiday_date === today);
              const isHoliday = holidayInfo?.holiday_type === 'holiday';
              const isCompensation = holidayInfo?.holiday_type === 'compensation';
              const effectiveDow = isCompensation && holidayInfo.compensation_day_of_week
                ? Number(holidayInfo.compensation_day_of_week) : rawDow;
              const isWeekend = rawDow === 0 || rawDow === 6;
              const isSchoolDay = (!isWeekend && !isHoliday) || isCompensation;

              if (isSchoolDay) {
                const lessons = schedules
                  .filter(s => Number(s.day_of_week) === effectiveDow)
                  .sort((a, b) => Number(a.period_number) - Number(b.period_number));

                // เพิ่มคาบโฮมรูมสำหรับห้องที่เป็นครูประจำชั้น (ถ้ายังไม่มีใน schedule)
                const myHomeroomCls = dbClassrooms.filter(c =>
                  String(c.homeroom_teacher_id) === String(user.id) || String(c.homeroom_teacher2_id) === String(user.id)
                );
                myHomeroomCls.forEach(cls => {
                  const hrPeriod = periodsList.find(p =>
                    Number(p.is_homeroom) && (!p.level_id || String(p.level_id) === String(cls.level_id))
                  );
                  if (!hrPeriod) return;
                  const exists = lessons.some(l =>
                    String(l.classroom_id) === String(cls.id) && String(l.period_id) === String(hrPeriod.id)
                  );
                  if (!exists) {
                    lessons.push({
                      classroom_id: cls.id,
                      period_id: hrPeriod.id,
                      period_number: hrPeriod.period_number,
                      period_name: hrPeriod.period_name,
                      start_time: hrPeriod.start_time,
                      end_time: hrPeriod.end_time,
                      is_homeroom: 1,
                      subject_name: 'โฮมรูม',
                      subject_code: null,
                      level_code: cls.level_code,
                      grade: cls.grade,
                      room: cls.room,
                      day_of_week: effectiveDow,
                    });
                  }
                });
                lessons.sort((a, b) => Number(a.period_number) - Number(b.period_number));

                setTodayLessons(lessons);
              }
            } catch { /* ignore */ }
          })());
        }

        // รอ batch 2 ทั้งหมด (แต่ไม่ block UI เพราะ setLoading(false) ข้างล่าง)
        await Promise.all(batch2);
      } catch (e) {}
      finally { setLoading(false); }
    };
    if (user) fetch();
  }, [user, isAcademic]);

  if (loading) return <div className="loading-screen"><div className="spinner"/><span>กำลังโหลด...</span></div>;

  const currentYearSubjects = subjects.filter(s => currentYear && String(s.academic_year_id) === String(currentYear.id));

  // รายวิชาของครูคนนี้: ถ้าเป็น academic/admin เห็นทั้งหมด, teacher/homeroom เห็นเฉพาะที่รับผิดชอบ (API กรองมาให้แล้ว)
  // เรียงตาม ระดับชั้น → ห้อง → ประเภทวิชา
  const typeOrder = { 'พื้นฐาน': 1, 'เพิ่มเติม': 2, 'กิจกรรม': 3 };
  const sortedSubjects = [...currentYearSubjects].sort((a, b) => {
    const ga = Number(a.grade) || 0, gb = Number(b.grade) || 0;
    if (ga !== gb) return ga - gb;
    const ra = Number(a.classroom_room) || 0, rb = Number(b.classroom_room) || 0;
    if (ra !== rb) return ra - rb;
    const ta = typeOrder[a.subject_type] || 9, tb = typeOrder[b.subject_type] || 9;
    if (ta !== tb) return ta - tb;
    return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
  });

  // กรอง
  const mySubjects = sortedSubjects.filter(s => {
    if (filterGrade && String(s.grade) !== filterGrade) return false;
    if (filterRoom && String(s.classroom_room) !== filterRoom) return false;
    return true;
  });

  // สร้าง list ชั้น/ห้อง สำหรับ dropdown
  const gradeOptions = [...new Set(currentYearSubjects.map(s => s.grade).filter(Boolean))].sort((a, b) => Number(a) - Number(b));
  const roomOptions = [...new Set(
    currentYearSubjects
      .filter(s => !filterGrade || String(s.grade) === filterGrade)
      .map(s => s.classroom_room)
      .filter(Boolean)
  )].sort((a, b) => Number(a) - Number(b));

  // ห้องเรียน: academic/admin เห็นทั้งหมด, teacher/homeroom เห็นเฉพาะห้องที่ตัวเองเป็นครูประจำชั้น
  const myClasses = isAcademic()
    ? displayRooms
    : displayRooms.filter(c =>
        String(c.homeroom_teacher_id) === String(user?.id) ||
        String(c.homeroom_teacher2_id) === String(user?.id)
      );

  return (
    <div>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1565C0, #42a5f5)',
        borderRadius: '16px',
        padding: '28px 32px',
        color: 'white',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
            👋 สวัสดี {user?.prefix}{user?.firstname} {user?.lastname}
          </h1>
          <p style={{ opacity: 0.9, fontSize: '15px' }}>
            {ROLE_LABELS[user?.role]} | ปีการศึกษา {currentYear?.year || '...'}
          </p>
        </div>
        <div style={{ fontSize: '48px' }}>
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#1565C0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
          </svg>
        </div>
      </div>

      {/* Stats */}
      {(() => {
        const stats = [
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1565C0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
            label: 'รายวิชาทั้งหมด', value: mySubjects.length, color: '#1565C0', bg: '#e3f2fd'
          },
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
            label: 'ห้องเรียน', value: isAcademic() ? displayRooms.length : myClasses.length, color: '#2e7d32', bg: '#e8f5e9'
          },
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
            label: 'ปีการศึกษา', value: currentYear?.year || '-', color: '#e65100', bg: '#fff3e0'
          },
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6a1b9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
            label: 'สถานะ', value: 'ปกติ', color: '#6a1b9a', bg: '#f3e5f5'
          },
        ];
        return (
          <div className="grid-4" style={{ marginBottom: '24px' }}>
            {stats.map((stat, i) => (
              <div key={i} className="card" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '52px', height: '52px', background: stat.bg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {stat.icon}
                </div>
                <div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: '13px', color: '#888' }}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Today's Teaching Schedule (Teacher only) */}
      {user?.role === 'teacher' && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <h2 className="page-title" style={{ margin: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:7,marginBottom:2}}>
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              คาบสอนวันนี้
              {todayLessons.length > 0 && <span style={{ fontSize: '13px', fontWeight: 400, color: '#999', marginLeft: 8 }}>({todayLessons.length} คาบ)</span>}
            </h2>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 12, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => navigate('/attendance')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              เช็คชื่อรายคาบ
            </button>
          </div>

          {todayLessons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 16px' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 10 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/>
              </svg>
              <div style={{ fontSize: 14, color: '#999', marginBottom: 12 }}>ไม่มีคาบสอนวันนี้</div>
              <button
                onClick={() => navigate('/attendance')}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 2px 8px rgba(25,118,210,0.25)',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ไปหน้าเช็คชื่อรายคาบ
              </button>
            </div>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {todayLessons.map((lesson, i) => {
              const isHomeroom = Number(lesson.is_homeroom) === 1;
              return (
                <div key={i}
                  onClick={() => navigate(`/attendance?classroom_id=${lesson.classroom_id}&period_id=${lesson.period_id}`)}
                  style={{
                    padding: '12px 14px', borderRadius: 12,
                    border: isHomeroom ? '1.5px solid #c8e6c9' : '1.5px solid #e0e0e0',
                    background: isHomeroom ? 'linear-gradient(135deg, #e8f5e9, #f1f8e9)' : '#fff',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: isHomeroom ? '#2e7d32' : '#1565c0',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      {isHomeroom ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#43a047" stroke="#43a047" strokeWidth="2"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4"/></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                      )}
                      คาบ {lesson.period_number}
                    </span>
                    <span style={{ fontSize: 11, color: '#999' }}>
                      {lesson.start_time?.slice(0, 5)}-{lesson.end_time?.slice(0, 5)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lesson.subject_name || 'โฮมรูม'}
                  </div>
                  <div style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    {lesson.level_code} {lesson.grade}/{lesson.room}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      onClick={(e) => { e.stopPropagation(); navigate(`/attendance?classroom_id=${lesson.classroom_id}&period_id=${lesson.period_id}`); }}
                      style={{
                        fontSize: 11, fontWeight: 600, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
                      }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      เช็คชื่อ
                    </span>
                    <span style={{ color: '#ddd' }}>|</span>
                    <span
                      onClick={(e) => { e.stopPropagation(); navigate(`/attendance-report?classroom_id=${lesson.classroom_id}&subject_id=${lesson.subject_id || ''}`); }}
                      style={{
                        fontSize: 11, fontWeight: 600, color: '#43a047', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
                      }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#43a047" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      รายงาน
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {/* My Subjects */}
      {(!isAcademic() ? mySubjects.length > 0 : true) && (user?.role !== 'homeroom') && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <h2 className="page-title" style={{ margin: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:7,marginBottom:2}}>
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              รายวิชาที่รับผิดชอบ
              <span style={{ fontSize: '13px', fontWeight: 400, color: '#999', marginLeft: 8 }}>({mySubjects.length} วิชา)</span>
            </h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={filterGrade} onChange={e => { setFilterGrade(e.target.value); setFilterRoom(''); }}
                style={{ padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #ddd', fontSize: '13px', background: '#fff', cursor: 'pointer', minWidth: 100 }}>
                <option value="">ทุกชั้น</option>
                {gradeOptions.map(g => <option key={g} value={g}>ป.{g}</option>)}
              </select>
              <select value={filterRoom} onChange={e => setFilterRoom(e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #ddd', fontSize: '13px', background: '#fff', cursor: 'pointer', minWidth: 100 }}>
                <option value="">ทุกห้อง</option>
                {roomOptions.map(r => <option key={r} value={r}>ห้อง {r}</option>)}
              </select>
              {(filterGrade || filterRoom) && (
                <button onClick={() => { setFilterGrade(''); setFilterRoom(''); }}
                  style={{ padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #e0e0e0', background: '#f5f5f5', fontSize: '12px', cursor: 'pointer', color: '#666' }}>
                  ✕ ล้างตัวกรอง
                </button>
              )}
            </div>
          </div>
          {mySubjects.length === 0 ? (
            <div className="text-center text-muted" style={{ padding: '40px' }}>ไม่มีรายวิชาในขณะนี้</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
              {mySubjects.map(sub => (
                <div key={sub.id} style={{
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  padding: '16px',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#2196F3'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '700', fontSize: '15px', color: '#1565C0' }}>{sub.subject_code}</span>
                    <span className={`badge badge-${sub.subject_type === 'พื้นฐาน' ? 'primary' : 'warning'}`}>
                      {sub.subject_type}
                    </span>
                  </div>
                  <p style={{ fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>{sub.subject_name}</p>
                  <p style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>
                    {sub.level_name} {/ม|มัธยม|junior|middle|senior|high/i.test(sub.level_code || sub.level_name || '') ? 'ม.' : 'ป.'}{sub.grade}{sub.classroom_room ? `/${sub.classroom_room}` : ''} | {sub.credits} หน่วยกิต
                  </p>
                  {sub.teacher_names && <p style={{ fontSize: '12px', color: '#666', display:'flex', alignItems:'center', gap:4, marginBottom: '6px' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    {sub.teacher_names}</p>}
                  {/* Progress + Quality */}
                  {sub.subject_type !== 'กิจกรรม' && (() => {
                    const total = Number(sub.student_count) || 0;
                    const sc1 = Number(sub.scored_sem1) || 0;
                    const sc2 = Number(sub.scored_sem2) || 0;
                    const pct1 = total > 0 ? Math.round(sc1 / total * 100) : 0;
                    const pct2 = total > 0 ? Math.round(sc2 / total * 100) : 0;
                    const hasSem1 = Number(sub.indicator_sem1) > 0;
                    const hasSem2 = Number(sub.indicator_sem2) > 0;
                    const dotColor = (pct) => pct === 100 ? '#43a047' : pct > 0 ? '#ff9800' : '#e0e0e0';
                    const subProg = qualProgress[sub.id] || {};
                    const hasQual = qTypes.length > 0 && total > 0;
                    if (!hasSem1 && !hasSem2 && !hasQual) return null;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px', padding: '6px 8px', background: '#f8f9fa', borderRadius: '8px', fontSize: '11px' }}>
                        {/* Score progress */}
                        {(hasSem1 || hasSem2) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }} title="ความคืบหน้าคะแนน">
                            <span style={{ fontSize: 12, flexShrink: 0 }}>📝</span>
                            {hasSem1 && <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(pct1), border: '1px solid rgba(0,0,0,0.08)' }} title={`คะแนน ภาค 1: ${pct1}%`}/>}
                            {hasSem2 && <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(pct2), border: '1px solid rgba(0,0,0,0.08)' }} title={`คะแนน ภาค 2: ${pct2}%`}/>}
                            <span style={{ fontSize: 9, color: '#999', whiteSpace: 'nowrap' }}>
                              {hasSem1 && hasSem2 ? `${pct1}/${pct2}` : hasSem1 ? `${pct1}%` : `${pct2}%`}
                            </span>
                          </div>
                        )}
                        {/* Quality progress */}
                        {hasQual && qTypes.map(t => {
                          const tProg = subProg[t.type_key] || {};
                          const s1 = tProg[1] || 0;
                          const s2 = tProg[2] || 0;
                          const qp1 = total > 0 ? Math.round(s1 / total * 100) : 0;
                          const qp2 = total > 0 ? Math.round(s2 / total * 100) : 0;
                          return (
                            <div key={t.type_key} style={{ display: 'flex', alignItems: 'center', gap: 3 }} title={t.label}>
                              <span style={{ fontSize: 12, flexShrink: 0 }}>{t.icon || '📋'}</span>
                              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(qp1), border: '1px solid rgba(0,0,0,0.08)' }} title={`${t.label} ภาค 1: ${qp1}%`}/>
                              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(qp2), border: '1px solid rgba(0,0,0,0.08)' }} title={`${t.label} ภาค 2: ${qp2}%`}/>
                              <span style={{ fontSize: 9, color: '#999', whiteSpace: 'nowrap' }}>{qp1}/{qp2}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'nowrap' }}>
                    {!/ม|มัธยม|junior|middle|senior|high/i.test(sub.level_code || sub.level_name || '') && (<>
                    {(sub.subject_name || '').includes('ชุมนุม') ? (
                    <button className="btn btn-outline btn-sm" style={{ flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate('/clubs')}>
                      🏫 จัดการชุมนุม
                    </button>
                    ) : (
                    <button className="btn btn-outline btn-sm" style={{ flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate(`/indicators/${sub.id}/${sub.classroom_id || 0}`)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3,marginBottom:1}}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                      ตัวชี้วัด
                    </button>
                    )}
                    <button className="btn btn-primary btn-sm" style={{ flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate(`/scores/${sub.id}/${sub.classroom_id || 0}`)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3,marginBottom:1}}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                      กรอกคะแนน
                    </button>
                    <button className="btn btn-success btn-sm" style={{ flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate(`/subject-result/${sub.id}/${sub.classroom_id || 0}`)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3,marginBottom:1}}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                      ผลการเรียน
                    </button>
                    {isSem2 && <button className="btn btn-sm" style={{ flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px', background: '#7b1fa2', color: '#fff', border: 'none' }}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('wadphol_token') || '';
                          const res = await fetch(
                            `${API_BASE}/scores/pp5_cover.php?subject_id=${sub.id}&classroom_id=${sub.classroom_id || 0}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          if (!res.ok) throw new Error('PDF error');
                          const blob = await res.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `ปพ5_ปก_${sub.subject_code || sub.id}_${sub.level_code || ''}${sub.grade || ''}-${sub.room || ''}.pdf`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch { toast.error('ดาวน์โหลดปก ปพ.5 ไม่สำเร็จ'); }
                      }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3,marginBottom:1}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      ปก ปพ.5
                    </button>}
                    </>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Classrooms (homeroom/teacher/academic) */}
      {(myClasses.length > 0) && (
        <div className="card">
          <h2 className="page-title" style={{ marginBottom: '16px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:7,marginBottom:2}}>
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            ห้องเรียน
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' }}>
            {myClasses.map(cls => (
              <div key={cls.key || cls.id} style={{
                border: '1.5px solid #e8e8e8',
                borderRadius: '12px',
                padding: '16px',
                cursor: cls.id ? 'pointer' : 'default',
                opacity: cls.id ? 1 : 0.55,
              }}
                onMouseEnter={e => { if (cls.id) e.currentTarget.style.borderColor = '#4CAF50'; }}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}
                onClick={() => cls.id && navigate(`/students/${cls.id}`)}
              >
                <div style={{ marginBottom: '8px' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#2e7d32' }}>
                  {cls.label}
                </div>
                <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                  {cls.level_name || ''}
                </div>
                {cls.homeroom1_name && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                    ครู: {cls.homeroom1_name}
                  </div>
                )}
                <div style={{ marginTop: '10px' }}>
                  <span className="badge badge-success">{cls.id ? 'จัดการนักเรียน →' : 'ยังไม่มีข้อมูล'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
